import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectsQueryScope } from "@/features/projects/hooks/useProjectsQuery";
import { projectQueryKeys } from "@/features/projects/query-keys";
import { PERSONAL_PROJECT_SCOPE } from "@/features/projects/types";
import { getActiveOrganizationId } from "@/utils/requestScope";
import { getKeycloakUserInfo } from "@/services/keycloak";
import { useProjects, type Project } from "./useProjects";

// Selection provenance only: project data still lives exclusively in the
// canonical includeShared Projects query. No MCP-owned project cache.
export const useMCPProjectTarget = (open: boolean) => {
  const { scope, initialized } = useProjectsQueryScope();
  const query = useProjects({ enabled: open });
  const client = useQueryClient();
  const identity = JSON.stringify(projectQueryKeys.listScope(scope));
  const lease = useMemo(() => ({ identity, initialized }), [identity, initialized]);
  const activeLease = useRef<typeof lease | null>(lease);
  const seedLease = useRef(lease);
  const [selection, setSelection] = useState<{ id: string; lease: typeof lease } | null>(null);

  useLayoutEffect(() => {
    activeLease.current = lease;
    // Saved project metadata belongs to the scope that opened this dialog.
    // Reseeding an already-open tool after a scope change must not rebind it.
    if (!open) seedLease.current = lease;
    return () => { activeLease.current = null; };
  }, [lease, open]);

  const selectProject = useCallback((id: string) => {
    if (activeLease.current) setSelection({ id, lease: activeLease.current });
  }, []);
  const seedProject = useCallback((id: string) => {
    if (seedLease.current !== activeLease.current) return;
    setSelection({ id, lease: seedLease.current });
  }, []);
  const selectedId = selection?.lease === lease ? selection.id : "";
  const queryKey = projectQueryKeys.list(scope, { includeShared: true });
  const isCurrentMember = () => {
    const state = client.getQueryState<Project[]>(queryKey);
    return open && initialized === true && Boolean(scope.principalId) &&
      selection?.lease === lease && Boolean(selectedId) &&
      !query.isPlaceholderData && state?.status === "success" &&
      state.fetchStatus === "idle" && !state.isInvalidated &&
      Array.isArray(state.data) && state.data.some((project) => project.id === selectedId);
  };
  // Hide an authoritative removal immediately, but retain the draft during a
  // same-scope refetch/error so a transient request cannot erase user input.
  const removed = query.isSuccess && !query.isFetching &&
    !query.data?.some((project) => project.id === selectedId);
  const projectId = removed ? "" : selectedId;
  const validateProject = () => {
    // These are the same live identity/storage sources used by AuthProvider
    // and OrganizationProvider. They close the interval before React commits
    // a principal/workspace transition; effects alone cannot authorize submit.
    const principal = getKeycloakUserInfo()?.id;
    const organization = getActiveOrganizationId() ?? PERSONAL_PROJECT_SCOPE;
    return Boolean(projectId) && activeLease.current === lease && principal === scope.principalId && organization === scope.organizationId && isCurrentMember();
  };

  return { projectId, selectProject, seedProject, isEligible: isCurrentMember(), validateProject };
};
